<!-- 휴가신청 (leave_request)
     - 기자: 신청서 작성 + 내 신청 목록(대기 취소 가능)
     - 데스크/관리자(level>=8): 결재 탭에서 전체 신청 승인·반려
     스타일은 Vuexy vs- 컴포넌트를 쓰는 기존 화면 관례를 따른다. -->
<template>
  <div id="leave-request">
    <vx-card>
      <!-- 탭: 내 신청 / 결재(데스크 이상) -->
      <vs-tabs>
        <!-- ===== 내 휴가신청 ===== -->
        <vs-tab label="내 휴가신청">
          <!-- 연차 현황. 근로기준법 제60조 기준으로 서버(/api/leave/summary)가 계산한다. -->
          <div v-if="summary" class="lr-summary mb-4">
            <div class="lr-box">
              <div class="lr-box-label">총 부여</div>
              <div class="lr-box-value">{{ summary.total_days }}<span class="lr-unit">일</span></div>
              <div class="lr-box-sub">
                법정 {{ summary.base_days }}
                <span v-if="summary.added_days > 0"> + 추가 {{ summary.added_days }}</span>
                <span v-if="summary.deducted_days > 0"> − 공제 {{ summary.deducted_days }}</span>
              </div>
            </div>
            <div class="lr-box">
              <div class="lr-box-label">결재 완료</div>
              <div class="lr-box-value">{{ summary.used_days }}<span class="lr-unit">일</span></div>
              <div class="lr-box-sub">사용한 연차</div>
            </div>
            <div class="lr-box">
              <div class="lr-box-label">신청 중</div>
              <div class="lr-box-value lr-pending-text">{{ summary.requesting_days }}<span class="lr-unit">일</span></div>
              <div class="lr-box-sub">결재 대기</div>
            </div>
            <div class="lr-box lr-box-strong">
              <div class="lr-box-label">잔여</div>
              <div class="lr-box-value">{{ summary.remain_days }}<span class="lr-unit">일</span></div>
              <div class="lr-box-sub">신청 중 제외 {{ summary.remain_after_pending }}일</div>
            </div>
            <div class="lr-box lr-box-wide">
              <div class="lr-box-label">사용 기간</div>
              <div class="lr-box-value lr-box-date">{{ summary.accrue_from }} ~ {{ summary.expire_on }}</div>
              <div class="lr-box-sub">
                <span :class="expireDday <= 60 ? 'lr-warn' : ''">
                  소멸까지 {{ expireDday }}일
                </span>
                <span v-if="summary.other_used_days > 0"> · 연차 외 휴가 {{ summary.other_used_days }}일</span>
              </div>
            </div>
          </div>
          <!-- 사용촉진 통보를 받은 경우 -->
          <vs-alert v-if="summary && summary.promotions && summary.promotions.length"
                    color="warning" class="mb-4" active="true">
            연차 사용촉진 통보가 있습니다 —
            <span v-for="(p, i) in summary.promotions" :key="i">
              <template v-if="i > 0"> · </template>{{ promotionStageText(p.stage) }} {{ p.acted_on }}
            </span>
          </vs-alert>

          <div class="vx-row">
            <!-- 신청서 -->
            <div class="vx-col md:w-1/3 w-full mb-4">
              <h5 class="mb-3">{{ editingId ? '휴가 신청 수정' : '휴가 신청' }}</h5>
              <div class="mb-3">
                <label class="text-sm">종류</label>
                <v-select v-model="form.leave_type" :clearable="false" label="text"
                          :options="leaveTypeOptions" :reduce="o => o.value" class="w-full" @input="onDateChange"/>
              </div>
              <div class="mb-3">
                <label class="text-sm">시작일</label>
                <datepicker v-model="form.start_date" :clearable="false" lang="kr" format="yyyy-MM-dd" class="w-full"
                            :highlighted="highlightedDates" @selected="onDateChange"/>
              </div>
              <div class="mb-3">
                <label class="text-sm">종료일</label>
                <datepicker v-model="form.end_date" :clearable="false" lang="kr" format="yyyy-MM-dd" class="w-full"
                            :highlighted="highlightedDates" @selected="onDateChange"/>
              </div>
              <div class="mb-3">
                <label class="text-sm">일수 <span class="text-xs" style="color:#888">(주말·공휴일 제외 자동계산, 수정 가능)</span></label>
                <vs-input type="number" step="0.5" min="0.5" v-model="form.days" class="w-full"/>
                <div v-if="spanHolidays.length" class="text-xs mt-1" style="color:#c0392b">
                  기간 내 공휴일: {{ spanHolidays.map(h => h.date.slice(5) + ' ' + h.name).join(', ') }}
                </div>
              </div>
              <div class="mb-3">
                <label class="text-sm">사유</label>
                <vs-textarea v-model="form.reason" rows="3"/>
              </div>
              <vs-button class="w-full" @click="submit" :disabled="submitting">{{ editingId ? '수정 후 재상신' : '신청' }}</vs-button>
              <vs-button v-if="editingId" class="w-full mt-2" color="warning" type="border" @click="cancelEdit">수정 취소</vs-button>
            </div>

            <!-- 내 신청 목록 -->
            <div class="vx-col md:w-2/3 w-full">
              <h5 class="mb-3">내 신청 내역</h5>
              <vs-table :data="myList" stripe>
                <template slot="thead">
                  <vs-th>종류</vs-th><vs-th>기간</vs-th><vs-th>일수</vs-th>
                  <vs-th>상태</vs-th><vs-th>결재/사유</vs-th><vs-th></vs-th>
                </template>
                <template slot-scope="{data}">
                  <vs-tr v-for="(r, i) in data" :key="i">
                    <vs-td>{{ typeText(r.leave_type) }}</vs-td>
                    <vs-td>{{ r.start_date }} ~ {{ r.end_date }}</vs-td>
                    <vs-td>{{ r.days }}</vs-td>
                    <vs-td><span :class="'lr-badge lr-' + r.status">{{ statusText(r.status) }}</span></vs-td>
                    <vs-td>
                      <span v-if="r.status === 'approved'">{{ r.approver_name }} 승인</span>
                      <span v-else-if="r.status === 'rejected'">반려: {{ r.reject_reason }}</span>
                      <span v-else>-</span>
                    </vs-td>
                    <vs-td>
                      <vs-button v-if="r.status === 'pending'" size="small" color="warning" type="border"
                                 @click="cancel(r.id)">취소</vs-button>
                      <!-- 반려된 신청은 수정해서 다시 올릴 수 있다. 대기 건도 수정 가능. -->
                      <vs-button v-if="r.status === 'rejected' || r.status === 'pending'" class="ml-2" size="small"
                                 color="primary" type="border" @click="startEdit(r)">수정</vs-button>
                    </vs-td>
                  </vs-tr>
                </template>
              </vs-table>
            </div>
          </div>
        </vs-tab>

        <!-- ===== 결재 (데스크/관리자) ===== -->
        <vs-tab v-if="isDesk" label="결재">
          <div class="flex items-center mb-3">
            <v-select v-model="approveFilter" :clearable="false" label="text" style="width:160px"
                      :options="filterOptions" :reduce="o => o.value" @input="loadAll"/>
            <vs-button class="ml-3" size="small" type="border" @click="loadAll">새로고침</vs-button>
          </div>
          <vs-table :data="allList" stripe>
            <template slot="thead">
              <vs-th>신청자</vs-th><vs-th>종류</vs-th><vs-th>기간</vs-th><vs-th>일수</vs-th>
              <vs-th>사유</vs-th><vs-th>상태</vs-th><vs-th>처리</vs-th>
            </template>
            <template slot-scope="{data}">
              <vs-tr v-for="(r, i) in data" :key="i">
                <vs-td>{{ r.member_name }}</vs-td>
                <vs-td>{{ typeText(r.leave_type) }}</vs-td>
                <vs-td>{{ r.start_date }} ~ {{ r.end_date }}</vs-td>
                <vs-td>{{ r.days }}</vs-td>
                <vs-td>{{ r.reason }}</vs-td>
                <vs-td><span :class="'lr-badge lr-' + r.status">{{ statusText(r.status) }}</span></vs-td>
                <vs-td>
                  <!-- 대기: 승인/반려. 이미 결재된 건도 데스크가 반대로 변경 가능(취소 제외). -->
                  <template v-if="r.status === 'pending'">
                    <vs-button size="small" color="success" @click="approve(r.id)">승인</vs-button>
                    <vs-button class="ml-2" size="small" color="danger" type="border" @click="reject(r.id)">반려</vs-button>
                  </template>
                  <template v-else-if="r.status === 'approved'">
                    <span class="mr-2">{{ r.approver_name }} 승인</span>
                    <vs-button size="small" color="danger" type="border" @click="reject(r.id)">반려로 변경</vs-button>
                  </template>
                  <template v-else-if="r.status === 'rejected'">
                    <span class="mr-2">{{ r.approver_name }} 반려</span>
                    <vs-button size="small" color="success" type="border" @click="approve(r.id)">승인으로 변경</vs-button>
                  </template>
                  <span v-else>{{ statusText(r.status) }}</span>
                </vs-td>
              </vs-tr>
            </template>
          </vs-table>
        </vs-tab>
      </vs-tabs>
    </vx-card>
  </div>
</template>

<script>
import vSelect from 'vue-select';
import Datepicker from 'vuejs-datepicker';
import moment from 'moment';

export default {
  components: { 'v-select': vSelect, datepicker: Datepicker },
  data() {
    return {
      submitting: false,
      editingId: null,
      holidays: [],        // [{date:'YYYY-MM-DD', name}]
      holidaySet: {},      // { 'YYYY-MM-DD': name }
      form: {
        leave_type: 'annual',
        start_date: new Date(),
        end_date: new Date(),
        days: 1,
        reason: '',
      },
      summary: null,       // /api/leave/summary 결과 (연차 현황)
      myList: [],
      allList: [],
      approveFilter: 'pending',
      leaveTypeOptions: [
        { text: '연차', value: 'annual' },
        { text: '오전 반차', value: 'half_am' },
        { text: '오후 반차', value: 'half_pm' },
        { text: '병가', value: 'sick' },
        { text: '경조사', value: 'event' },
        { text: '기타', value: 'etc' },
      ],
      filterOptions: [
        { text: '대기', value: 'pending' },
        { text: '승인', value: 'approved' },
        { text: '반려', value: 'rejected' },
        { text: '전체', value: '' },
      ],
      typeMap: { annual: '연차', half_am: '오전반차', half_pm: '오후반차', sick: '병가', event: '경조사', etc: '기타' },
      statusMap: { pending: '대기', approved: '승인', rejected: '반려', canceled: '취소' },
    };
  },
  computed: {
    me() { return this.$store.state.AppActiveUser; },
    isDesk() { return this.me.level * 1 >= 8; },
    // 소멸까지 남은 일수. 제60조⑦ — 발생일로부터 1년간 쓰지 않으면 소멸한다.
    expireDday() {
      if (!this.summary || !this.summary.expire_on) return 0;
      const exp = new Date(this.summary.expire_on + 'T00:00:00');
      const today = new Date(); today.setHours(0, 0, 0, 0);
      return Math.max(0, Math.round((exp - today) / 86400000));
    },
    // 달력에 공휴일 하이라이트 (vuejs-datepicker 는 dates 배열/customPredictor 지원)
    highlightedDates() {
      return {
        dates: this.holidays.map(h => new Date(h.date + 'T00:00:00')),
        customPredictor: (d) => d.getDay() === 0 || d.getDay() === 6, // 주말
      };
    },
    // 현재 선택 기간에 걸린 공휴일 목록
    spanHolidays() {
      if (!this.form.start_date || !this.form.end_date) return [];
      const s = this.fmt(this.form.start_date), e = this.fmt(this.form.end_date);
      return this.holidays.filter(h => h.date >= s && h.date <= e);
    },
  },
  methods: {
    fmt(d) { return moment(d).format('YYYY-MM-DD'); },
    typeText(t) { return this.typeMap[t] || t; },
    loadHolidays() {
      this.$http.post('/api/leave/holidays', {})
        .then(res => {
          this.holidays = res.data || [];
          this.holidaySet = {};
          for (const h of this.holidays) this.holidaySet[h.date] = h.name;
        })
        .catch(() => {});
    },
    // 주말·공휴일을 뺀 영업일 수. 반차(half_*)면 0.5.
    businessDays(start, end) {
      if (this.form.leave_type === 'half_am' || this.form.leave_type === 'half_pm') return 0.5;
      let n = 0;
      const cur = moment(start).startOf('day');
      const last = moment(end).startOf('day');
      while (cur.isSameOrBefore(last)) {
        const dow = cur.day();
        const key = cur.format('YYYY-MM-DD');
        if (dow !== 0 && dow !== 6 && !this.holidaySet[key]) n++;
        cur.add(1, 'day');
      }
      return n;
    },
    // 날짜/종류 변경 시 일수 자동계산
    onDateChange() {
      this.$nextTick(() => {
        if (this.form.start_date && this.form.end_date &&
            moment(this.form.end_date).isSameOrAfter(this.form.start_date)) {
          this.form.days = this.businessDays(this.form.start_date, this.form.end_date);
        }
      });
    },
    statusText(s) { return this.statusMap[s] || s; },
    loginRedirect(error) {
      if (error && error.toString().includes('401')) {
        alert('로그인 해주세요!');
        this.$router.push({ path: '/pages/login?to=' + this.$router.history.current.path });
      }
    },
    loadMine() {
      this.$http.post('/api/leave/list', { member_id: this.me.id, scope: 'mine' })
        .then(res => { this.myList = res.data; })
        .catch(e => this.loginRedirect(e));
    },
    // 연차 현황. 계산은 전부 서버에서 한다 —
    // 데일리안은 화면에서 하루씩 순회해 계산했고 그래서 25일 상한이 빠지고
    // 추가·공제를 채우지 못했다.
    loadSummary() {
      this.$http.post('/api/leave/summary', { member_id: this.me.id })
        .then(res => { this.summary = res.data; })
        .catch(e => this.loginRedirect(e));
    },
    promotionStageText(stage) {
      if (stage === 'notify') return '1차 서면촉구';
      if (stage === 'employee_reply') return '사용시기 통보';
      if (stage === 'designate') return '사용시기 지정통보';
      return stage;
    },
    loadAll() {
      if (!this.isDesk) return;
      this.$http.post('/api/leave/list', { scope: 'all', status: this.approveFilter })
        .then(res => { this.allList = res.data; })
        .catch(e => this.loginRedirect(e));
    },
    startEdit(r) {
      // 반려/대기 신청을 폼에 불러와 수정 모드로 전환한다.
      this.editingId = r.id;
      this.form.leave_type = r.leave_type;
      this.form.start_date = new Date(r.start_date);
      this.form.end_date = new Date(r.end_date);
      this.form.days = r.days;
      this.form.reason = r.reason;
    },
    cancelEdit() {
      this.editingId = null;
      this.form = { leave_type: 'annual', start_date: new Date(), end_date: new Date(), days: 1, reason: '' };
    },
    submit() {
      if (!this.form.start_date || !this.form.end_date) { return this.notify('입력', '기간을 선택하세요', 'warning'); }
      if (moment(this.form.end_date).isBefore(this.form.start_date)) { return this.notify('입력', '종료일이 시작일보다 빠릅니다', 'warning'); }
      this.submitting = true;
      const isEdit = !!this.editingId;
      const url = isEdit ? '/api/leave/update' : '/api/leave/submit';
      const payload = {
        member_id: this.me.id,
        leave_type: this.form.leave_type,
        start_date: moment(this.form.start_date).format('YYYY-MM-DD'),
        end_date: moment(this.form.end_date).format('YYYY-MM-DD'),
        days: this.form.days,
        reason: this.form.reason,
      };
      if (isEdit) payload.id = this.editingId;
      this.$http.post(url, payload).then(() => {
        this.submitting = false;
        this.notify('완료', isEdit ? '수정되어 다시 상신되었습니다' : '휴가 신청이 등록되었습니다', 'success');
        this.cancelEdit();
        this.loadMine();
      }).catch(e => { this.submitting = false; this.loginRedirect(e); this.notify('오류', isEdit ? '수정 실패' : '신청 실패', 'danger'); });
    },
    cancel(id) {
      if (!confirm('신청을 취소하시겠습니까?')) return;
      this.$http.post('/api/leave/cancel', { id, member_id: this.me.id })
        .then(() => { this.notify('완료', '취소되었습니다', 'success'); this.loadMine(); })
        .catch(e => { this.loginRedirect(e); this.notify('오류', '취소 실패', 'danger'); });
    },
    approve(id) {
      this.$http.post('/api/leave/approve', { id })
        .then(() => { this.notify('완료', '승인되었습니다', 'success'); this.loadAll(); })
        .catch(e => { this.loginRedirect(e); this.notify('오류', '승인 실패', 'danger'); });
    },
    reject(id) {
      const reason = prompt('반려 사유를 입력하세요');
      if (reason === null) return;
      this.$http.post('/api/leave/reject', { id, reject_reason: reason })
        .then(() => { this.notify('완료', '반려되었습니다', 'success'); this.loadAll(); })
        .catch(e => { this.loginRedirect(e); this.notify('오류', '반려 실패', 'danger'); });
    },
    notify(title, text, color) {
      this.$vs.notify({ title, text, color, iconPack: 'feather', icon: 'icon-alert-circle' });
    },
  },
  created() {
    this.loadHolidays();
    this.loadMine();
    this.loadSummary();
    if (this.isDesk) this.loadAll();
  },
};
</script>

<style scoped>
.lr-summary { display: flex; flex-wrap: wrap; gap: 12px; }
.lr-box {
  flex: 1 1 130px; min-width: 130px; padding: 12px 14px;
  border: 1px solid #e4e4e7; border-radius: 6px; background: #fafafa;
}
.lr-box-wide { flex: 2 1 260px; }
.lr-box-strong { border-color: #7367f0; background: #f4f3ff; }
.lr-box-label { font-size: 12px; color: #6b6b76; margin-bottom: 4px; }
.lr-box-value { font-size: 22px; font-weight: 600; line-height: 1.2; }
.lr-box-date { font-size: 14px; font-weight: 500; }
.lr-unit { font-size: 13px; font-weight: 400; margin-left: 2px; }
.lr-box-sub { font-size: 11px; color: #8a8a94; margin-top: 4px; }
.lr-pending-text { color: #f0a020; }
.lr-warn { color: #c0392b; font-weight: 600; }
.lr-badge { padding: 2px 8px; border-radius: 4px; font-size: 12px; color: #fff; }
.lr-pending  { background: #f0a020; }
.lr-approved { background: #2d8a34; }
.lr-rejected { background: #c0392b; }
.lr-canceled { background: #888; }
</style>
